import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, MapPin, FileText, Upload, Calendar, Plus, Trash2, Download, AlertTriangle, Pencil } from 'lucide-react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { ErrorDebugModal } from '../components/ErrorDebugModal';
import { CodeNAFAutocomplete } from '../components/CodeNAFAutocomplete';
import { TableauRecapConsommations } from '../components/TableauRecapConsommations';
import ModalBasculeSIRET from '../components/ModalBasculeSIRET';
import TranchesEligibilite from '../components/TranchesEligibilite';
import { Step4Sites } from '../components/ticfe/Step4Sites';
import { Step5Consommations } from '../components/ticfe/Step5Consommations';
import { Step6Validation } from '../components/ticfe/Step6Validation';
import { Step1Type, Step2ModeCalcul, Step3InfosEntreprise } from '../components/ticfe/Steps123';
import {
  getBrouillon as apiGetBrouillon,
  saveBrouillon as apiSaveBrouillon,
  deleteBrouillon as apiDeleteBrouillon,
  migrateLocalStorageBrouillons,
} from '../utils/brouillonsApi';

const CreateRapport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [rapportType, setRapportType] = useState(''); // 'mono' ou 'multi'
  const [modeCalcul, setModeCalcul] = useState(''); // 'siren' ou 'siret' (pour multi-site)
  
  const [formData, setFormData] = useState({
    // SIREN (Entreprise)
    siren: '',
    nom_entreprise: '',
    valeur_ajoutee_siren: '',
    debut_periode: '',
    fin_periode: '',
    commentaire_global: '', // Commentaire global au niveau SIREN
    
    // Commercial
    agence_id: '',
    commercial_id: '',
    
    // PDL (mono-site peut avoir plusieurs PDL)
    pdls: [], // [{pdl, puissance_souscrite, code_naf}]
    
    // Sites/PDL (multi-site)
    sites: []
  });

  const [currentPDL, setCurrentPDL] = useState({
    pdl: '',
    puissance_souscrite: '',
    code_naf: '',
    tranches_eligibilite: []
  });
  const [editingPDLIndex, setEditingPDLIndex] = useState(null);

  // Multi-sites
  const [showAddSite, setShowAddSite] = useState(false);
  const [showImportSites, setShowImportSites] = useState(false);
  const [architectureImportee, setArchitectureImportee] = useState(null);
  const [currentSite, setCurrentSite] = useState({
    siret: '',
    nom_site: '',
    valeur_ajoutee_siret: '',
    commentaire: '',
    taux_reduit_deja_applique: false, // true = ce site bénéficie déjà du taux réduit TICFE
    pdls: []
  });
  const [currentPDLMulti, setCurrentPDLMulti] = useState({
    pdl: '',
    puissance_souscrite: '',
    code_naf: '',
    domaine_tension_override: '',
    tranches_eligibilite: []
  });
  const [editingPDLMultiIndex, setEditingPDLMultiIndex] = useState(null);

  // Consommations par PDL : {pdl_number: {month_key: kwh_value}}
  const [consommationsParPDL, setConsommationsParPDL] = useState({});
  const [methodeConsommation, setMethodeConsommation] = useState('manuel'); // 'manuel' ou 'excel'
  const [showTableauRecap, setShowTableauRecap] = useState(false);
  const [errorDebug, setErrorDebug] = useState(null);
  const [errorContext, setErrorContext] = useState(null);
  
  // Brouillons
  const [brouillonId, setBrouillonId] = useState(null);
  
  // Répartition VAC
  const [showRepartitionVAC, setShowRepartitionVAC] = useState(false);
  const [modeRepartition, setModeRepartition] = useState('uniforme'); // 'uniforme', 'prorata', 'manuel'
  const [repartitionManuelle, setRepartitionManuelle] = useState({});
  
  // Bascule SIRET (quand SIREN non éligible)
  const [showModalBasculeSIRET, setShowModalBasculeSIRET] = useState(false);
  const [eligibiliteData, setEligibiliteData] = useState(null);
  const [resultatsParSiret, setResultatsParSiret] = useState(null);
  const [isCalculatingSiret, setIsCalculatingSiret] = useState(false);
  const [modeRapport, setModeRapport] = useState('siren'); // 'siren' ou 'siret'
  
  // Données référentielles
  const [agences, setAgences] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  
  // Mode édition
  const [editMode, setEditMode] = useState(false);
  const [editRapportId, setEditRapportId] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Cache des types d'éligibilité NAF (TICFE) pour l'étape 6
  const [nafEligMapTicfe, setNafEligMapTicfe] = useState({});

  useEffect(() => {
    loadReferentiels();
    
    // Mode édition : charger rapport existant
    const editId = searchParams.get('edit');
    if (editId) {
      chargerRapportPourEdition(editId);
      return;
    }
    
    // Charger brouillon si URL contient ID
    const urlParams = new URLSearchParams(window.location.search);
    const brouillonParam = urlParams.get('brouillon');
    if (brouillonParam) {
      chargerBrouillon(brouillonParam);
    }
    
    // Pré-remplir si SIREN et nom passés en paramètres (depuis Fiche Client)
    const sirenParam = searchParams.get('siren');
    const nomParam = searchParams.get('nom');
    if (sirenParam || nomParam) {
      setFormData(prev => ({
        ...prev,
        siren: sirenParam || prev.siren,
        nom_entreprise: nomParam || prev.nom_entreprise
      }));
    }
  }, [searchParams]);

  // === Fetch NAF eligibility when reaching Step 6 (TICFE) ===
  useEffect(() => {
    if (step !== 6) return;
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const pdlsAll = rapportType === 'multi'
      ? formData.sites.flatMap(s => (s.pdls || []))
      : (formData.pdls || []);
    const codes = [...new Set(pdlsAll.map(p => (p.code_naf || '').trim().toUpperCase()).filter(Boolean))];
    const toFetch = codes.filter(c => !(c in nafEligMapTicfe));
    if (toFetch.length === 0) return;
    (async () => {
      const updates = {};
      await Promise.all(toFetch.map(async (code) => {
        try {
          const res = await fetch(`${API_URL}/api/ticfe/naf/${encodeURIComponent(code)}/eligibilite`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            updates[code] = data.eligibility_type || null;
          } else {
            updates[code] = null;
          }
        } catch { updates[code] = null; }
      }));
      setNafEligMapTicfe(prev => ({ ...prev, ...updates }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rapportType, formData.pdls, formData.sites]);

  const chargerRapportPourEdition = async (rapportId) => {
    setLoadingEdit(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/rapports/${rapportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rapport = response.data;
      
      setEditMode(true);
      setEditRapportId(rapportId);
      
      // Déterminer le type de rapport
      const type = rapport.type_rapport === 'mono' ? 'mono' : 'multi';
      setRapportType(type);
      setModeCalcul(rapport.mode_calcul || 'siren');
      setModeRapport(rapport.mode_calcul || 'siren');
      
      // Reconstruire les PDLs depuis pdls_data
      const pdls = (rapport.pdls_data || []).map(p => ({
        pdl: p.pdl,
        puissance_souscrite: p.puissance_souscrite || '',
        code_naf: p.code_naf || '',
        tranches_eligibilite: p.tranches_detail || p.tranches_eligibilite || []
      }));
      
      // Reconstruire les sites si multi
      let sites = [];
      if (type === 'multi') {
        const sitesMap = {};
        (rapport.pdls_data || []).forEach(p => {
          const siret = p.site_siret || 'unknown';
          if (!sitesMap[siret]) {
            sitesMap[siret] = {
              siret,
              nom_site: p.site_nom || '',
              taux_reduit_deja_applique: p.taux_reduit_deja_applique || false,
              pdls: []
            };
          }
          sitesMap[siret].pdls.push({
            pdl: p.pdl,
            puissance_souscrite: p.puissance_souscrite || '',
            code_naf: p.code_naf || '',
            tranches_eligibilite: p.tranches_detail || p.tranches_eligibilite || []
          });
        });
        sites = Object.values(sitesMap);
      }
      
      // Pré-remplir le formulaire
      setFormData({
        siren: rapport.siren || '',
        nom_entreprise: rapport.nom_entreprise || '',
        valeur_ajoutee_siren: rapport.valeur_ajoutee || '',
        debut_periode: rapport.debut_periode ? rapport.debut_periode.split('T')[0] : '',
        fin_periode: rapport.fin_periode ? rapport.fin_periode.split('T')[0] : '',
        commentaire_global: rapport.commentaire_global || '',
        agence_id: rapport.agence_id || '',
        commercial_id: rapport.commercial_id || '',
        pdls: type === 'mono' ? pdls : [],
        sites: type === 'multi' ? sites : []
      });
      
      // Pré-remplir les consommations si disponibles
      if (rapport.consommations_par_pdl && Object.keys(rapport.consommations_par_pdl).length > 0) {
        setConsommationsParPDL(rapport.consommations_par_pdl);
      }
      
      // Aller à l'étape des consommations (step 3 pour mono, step 4 pour multi)
      setStep(type === 'mono' ? 3 : 4);
      
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
      alert('Erreur lors du chargement du rapport pour modification.');
    } finally {
      setLoadingEdit(false);
    }
  };

  const loadReferentiels = async () => {
    // Chargement depuis l'API (priorité) avec fallback minimaliste si offline
    try {
      const token = localStorage.getItem('token');
      const [ra, rc] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agences`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commerciaux`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (ra.ok && rc.ok) {
        const [dataA, dataC] = await Promise.all([ra.json(), rc.json()]);
        if (Array.isArray(dataA) && dataA.length) setAgences(dataA);
        if (Array.isArray(dataC) && dataC.length) setCommerciaux(dataC);
        return;
      }
    } catch (e) {
      console.warn('Chargement agences/commerciaux échoué', e);
    }
    // Fallback : pas de données
    setAgences([]);
    setCommerciaux([]);
  };

  const commerciauxFiltres = formData.agence_id 
    ? commerciaux.filter(c => c.agence_id === formData.agence_id)
    : commerciaux;

  // === GESTION BROUILLONS ===
  // Migration silencieuse depuis localStorage (1 fois max par utilisateur)
  useEffect(() => {
    migrateLocalStorageBrouillons('ticfe', (b) => ({
      nom_entreprise: b.formData?.nom_entreprise || '',
      siren: b.formData?.siren || '',
      step: b.step || 1,
      payload: {
        step: b.step,
        rapportType: b.rapportType,
        modeCalcul: b.modeCalcul,
        formData: b.formData,
        currentPDL: b.currentPDL,
        currentSite: b.currentSite,
        currentPDLMulti: b.currentPDLMulti,
        consommationsParPDL: b.consommationsParPDL,
        methodeConsommation: b.methodeConsommation,
      },
    })).catch(() => {});
  }, []);

  const sauvegarderBrouillon = async () => {
    try {
      const saved = await apiSaveBrouillon({
        id: brouillonId || undefined,
        type: 'ticfe',
        nom_entreprise: formData.nom_entreprise || '',
        siren: formData.siren || '',
        step,
        payload: {
          step,
          rapportType,
          modeCalcul,
          formData,
          currentPDL,
          currentSite,
          currentPDLMulti,
          consommationsParPDL,
          methodeConsommation,
        },
      });
      setBrouillonId(saved.id);
      alert(`✓ Brouillon sauvegardé\n\nÉtape ${step} - ${rapportType === 'mono' ? 'Mono-site' : 'Multi-site'}\n\nVous pouvez reprendre plus tard depuis le dashboard`);
    } catch (e) {
      console.error('Erreur sauvegarde brouillon:', e);
      alert('Erreur lors de la sauvegarde du brouillon.');
    }
  };

  const chargerBrouillon = async (id) => {
    try {
      const brouillon = await apiGetBrouillon(id);
      if (!brouillon) {
        alert('Brouillon introuvable');
        return;
      }
      const p = brouillon.payload || {};
      // Restaurer état
      setStep(p.step || brouillon.step || 1);
      setRapportType(p.rapportType || 'mono');
      setModeCalcul(p.modeCalcul || '');
      if (p.formData) setFormData(p.formData);
      if (p.currentPDL) setCurrentPDL(p.currentPDL);
      if (p.currentSite) setCurrentSite(p.currentSite);
      if (p.currentPDLMulti) setCurrentPDLMulti(p.currentPDLMulti);
      if (p.consommationsParPDL) setConsommationsParPDL(p.consommationsParPDL);
      if (p.methodeConsommation) setMethodeConsommation(p.methodeConsommation);
      setBrouillonId(brouillon.id);
      alert(`✓ Brouillon chargé\n\nReprise à l'étape ${p.step || brouillon.step || 1}`);
    } catch (e) {
      console.error('Erreur chargement brouillon:', e);
      alert('Erreur lors du chargement du brouillon.');
    }
  };

  const supprimerBrouillon = async () => {
    if (!brouillonId) return;
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    try {
      await apiDeleteBrouillon(brouillonId);
      setBrouillonId(null);
      alert('✓ Brouillon supprimé');
    } catch (e) {
      console.error('Erreur suppression brouillon:', e);
      alert('Erreur lors de la suppression du brouillon.');
    }
  };

  const addPDLToSite = () => {
    if (!currentPDLMulti.pdl || !currentPDLMulti.puissance_souscrite || !currentPDLMulti.code_naf) {
      alert('Remplissez tous les champs du PDL');
      return;
    }
    
    if (editingPDLMultiIndex !== null) {
      const newPdls = [...currentSite.pdls];
      newPdls[editingPDLMultiIndex] = {...currentPDLMulti};
      setCurrentSite({ ...currentSite, pdls: newPdls });
      setEditingPDLMultiIndex(null);
    } else {
      setCurrentSite({
        ...currentSite,
        pdls: [...currentSite.pdls, {...currentPDLMulti}]
      });
    }
    setCurrentPDLMulti({ pdl: '', puissance_souscrite: '', code_naf: '', domaine_tension_override: '', tranches_eligibilite: [] });
  };

  const addSiteToRapport = () => {
    if (!currentSite.siret) {
      alert('SIRET obligatoire');
      return;
    }
    
    if (modeCalcul === 'siret' && !currentSite.valeur_ajoutee_siret) {
      alert('Valeur Ajoutée obligatoire en mode site par site');
      return;
    }
    
    if (currentSite.pdls.length === 0) {
      alert('Ajoutez au moins un PDL à ce site');
      return;
    }
    
    setFormData({
      ...formData,
      sites: [...formData.sites, {...currentSite}]
    });
    
    // Réinitialiser pour ajouter un nouveau site (ne pas fermer formulaire)
    setCurrentSite({ siret: '', nom_site: '', valeur_ajoutee_siret: '', commentaire: '', pdls: [] });
    alert(`✓ Site ajouté avec ${currentSite.pdls.length} PDL\n\nVous pouvez ajouter un autre site ou continuer`);
  };

  const downloadTemplateSites = async () => {
    // Récupérer codes NAF
    const codesNAF = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/eligibility-rules`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()).catch(() => []);
    
    const wb = XLSX.utils.book_new();
    
    // Onglet 1 : Sites et PDL (principal)
    const dataPrincipal = [
      ['SIRET (14 chiffres)', 'Nom Site', 'VAC Site (€)', 'PDL', 'Puissance (kVA)', 'Code NAF', 'Libellé NAF', 'Éligibilité'],
      ['12345678901234', 'Site Production', modeCalcul === 'siret' ? '5000000' : '', '30001234', '250', 'C1392', '', ''],
      ['12345678901234', 'Site Production', '', '30001235', '150', 'C1071', '', ''],
      ['98765432109876', 'Site Commercial', modeCalcul === 'siret' ? '3000000' : '', '30009999', '36', 'C2229', '', '']
    ];
    
    const wsPrincipal = XLSX.utils.aoa_to_sheet(dataPrincipal);
    
    // Ajouter formules pour colonnes G et H (lignes 2+)
    for (let i = 2; i <= 10; i++) {
      // Formule G : VLOOKUP libellé
      wsPrincipal[`G${i}`] = { 
        f: `VLOOKUP(F${i},'Codes NAF'!A:B,2,FALSE)`,
        t: 's'
      };
      // Formule H : VLOOKUP éligibilité
      wsPrincipal[`H${i}`] = { 
        f: `VLOOKUP(F${i},'Codes NAF'!A:C,3,FALSE)`,
        t: 's'
      };
    }
    
    wsPrincipal['!cols'] = [
      { wch: 18 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 40 }, { wch: 18 }
    ];
    
    XLSX.utils.book_append_sheet(wb, wsPrincipal, 'Sites et PDL');
    
    // Onglet 2 : Référence Codes NAF (pour VLOOKUP)
    const dataReference = [
      ['Code NAF', 'Libellé', 'Éligibilité Potentielle']
    ];
    
    codesNAF.forEach(code => {
      const eligibilite = code.eligibility_type === 'EXONERATION' ? 'Exonération totale' :
                         code.eligibility_type === 'TARIF_REDUIT' ? 'Tarif réduit' :
                         'Non concerné';
      dataReference.push([code.naf_code_label, code.libelle, eligibilite]);
    });
    
    const wsReference = XLSX.utils.aoa_to_sheet(dataReference);
    wsReference['!cols'] = [{ wch: 10 }, { wch: 50 }, { wch: 20 }];
    
    XLSX.utils.book_append_sheet(wb, wsReference, 'Codes NAF');
    
    XLSX.writeFile(wb, `template_sites_pdl_${formData.siren || 'rapport'}.xlsx`);
    
    alert('✓ Template téléchargé\n\n2 onglets :\n- Sites et PDL : À remplir (A-F)\n- Codes NAF : Référence (auto)\n\nColonnes G-H : Formules automatiques\nModifiez code NAF → Libellé/Éligibilité se mettent à jour !');
  };

  const handleImportSitesExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: false });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        
        const sitesMap = {};
        const vacBySiret = {}; // Tracker VAC par SIRET pour validation
        
        for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) {
          const row = [];
          for (let colNum = 0; colNum <= 5; colNum++) {
            const cell = worksheet[XLSX.utils.encode_cell({r: rowNum, c: colNum})];
            row.push(cell ? cell.v : null);
          }
          
          const siret = row[0] ? String(row[0]).trim() : null;
          const nomSite = row[1] ? String(row[1]) : '';
          const vacSite = row[2] ? parseFloat(String(row[2]).replace(/\s/g, '')) : 0;
          const pdl = row[3] ? String(row[3]) : null;
          const puissance = row[4] ? parseFloat(row[4]) : 0;
          const codeNaf = row[5] ? String(row[5]) : null;
          
          if (!siret || !pdl || !puissance || !codeNaf) continue;
          
          // Validation VAC : même valeur pour tous PDL d'un SIRET
          if (vacSite > 0) {
            if (vacBySiret[siret] && vacBySiret[siret] !== vacSite) {
              alert(`⚠️ Erreur ligne ${rowNum + 1}\n\nSIRET ${siret} a plusieurs VAC différentes :\n- ${vacBySiret[siret].toLocaleString('fr-FR')} €\n- ${vacSite.toLocaleString('fr-FR')} €\n\nLa VAC doit être identique pour tous les PDL d'un même SIRET.`);
              return;
            }
            vacBySiret[siret] = vacSite;
          }
          
          if (!sitesMap[siret]) {
            sitesMap[siret] = {
              siret: siret,
              nom_site: nomSite,
              valeur_ajoutee_siret: vacBySiret[siret] || '', // Prendre première occurrence
              pdls: []
            };
          } else {
            // Vérifier cohérence VAC (ne doit pas changer pour même SIRET)
            if (vacSite > 0 && sitesMap[siret].valeur_ajoutee_siret > 0 && 
                Math.abs(vacSite - sitesMap[siret].valeur_ajoutee_siret) > 1) {
              console.warn(`⚠️ VAC différentes pour SIRET ${siret}: ${sitesMap[siret].valeur_ajoutee_siret} vs ${vacSite}`);
            }
          }
          
          sitesMap[siret].pdls.push({
            pdl: pdl,
            puissance_souscrite: puissance,
            code_naf: codeNaf
          });
        }
        
        const sitesArray = Object.values(sitesMap);
        
        setArchitectureImportee({
          sites: sitesArray,
          nb_sites: sitesArray.length,
          nb_pdl_total: sitesArray.reduce((sum, s) => sum + s.pdls.length, 0)
        });
        
        setShowImportSites(true);
        
      } catch (error) {
        console.error('Erreur import:', error);
        alert('❌ Erreur lecture fichier Excel');
      }
    };
    
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const validerArchitecture = () => {
    setFormData({
      ...formData,
      sites: architectureImportee.sites
    });
    setShowImportSites(false);
    setArchitectureImportee(null);
    alert(`✓ Architecture validée\n${architectureImportee.nb_sites} sites, ${architectureImportee.nb_pdl_total} PDL`);
  };

  const repartirVACAutomatique = () => {
    if (!formData.valeur_ajoutee_siren) {
      alert('Saisissez d\'abord la VAC totale du SIREN (étape 3)');
      return;
    }

    const vacTotale = parseFloat(formData.valeur_ajoutee_siren);
    const sites = architectureImportee.sites;
    
    let sitesAvecVAC;
    
    if (modeRepartition === 'uniforme') {
      // Répartition équitable sur tous les PDL
      const nbPDLTotal = sites.reduce((sum, s) => sum + s.pdls.length, 0);
      sitesAvecVAC = sites.map(site => ({
        ...site,
        valeur_ajoutee_siret: (vacTotale * site.pdls.length / nbPDLTotal).toFixed(2)
      }));
    } else if (modeRepartition === 'prorata') {
      // Répartition prorata consommation (nécessite consommations)
      alert('Répartition prorata consommation à implémenter après saisie consommations');
      return;
    } else if (modeRepartition === 'manuel') {
      // Utiliser pourcentages manuels
      sitesAvecVAC = sites.map((site, idx) => ({
        ...site,
        valeur_ajoutee_siret: (vacTotale * (parseFloat(repartitionManuelle[idx] || 0) / 100)).toFixed(2)
      }));
    }
    
    setArchitectureImportee({
      ...architectureImportee,
      sites: sitesAvecVAC
    });
    
    setShowRepartitionVAC(false);
    alert(`✓ VAC répartie (mode: ${modeRepartition})\n\nVAC totale: ${vacTotale.toLocaleString('fr-FR')} €`);
  };


  // Générer les mois de la période fiscale
  const generateMonthsForPeriod = () => {
    if (!formData.debut_periode || !formData.fin_periode) return [];
    
    const months = [];
    const start = new Date(formData.debut_periode);
    const end = new Date(formData.fin_periode);
    
    let current = new Date(start);
    while (current <= end) {
      months.push({
        label: current.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
        value: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`
      });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const updateConsommation = (pdlNumber, monthKey, value) => {
    const newConso = { ...consommationsParPDL };
    if (!newConso[pdlNumber]) newConso[pdlNumber] = {};
    newConso[pdlNumber][monthKey] = parseFloat(value) || 0;
    setConsommationsParPDL(newConso);
  };

  const handlePasteConsommations = (pdlNumber, event) => {
    event.preventDefault();
    
    // Récupérer données collées
    const pastedData = event.clipboardData.getData('text');
    const lines = pastedData.split('\n');
    const values = [];
    
    // Parser les valeurs (gérer virgule et espaces)
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed) {
        const val = parseFloat(trimmed.replace(',', '.').replace(/\s/g, ''));
        if (!isNaN(val)) {
          values.push(val);
        }
      }
    });
    
    if (values.length === 0) {
      alert('Aucune valeur numérique trouvée dans le collage');
      return;
    }
    
    // Remplir les mois dans l'ordre
    const months = generateMonthsForPeriod();
    const newConso = { ...consommationsParPDL };
    if (!newConso[pdlNumber]) newConso[pdlNumber] = {};
    
    values.forEach((val, index) => {
      if (index < months.length) {
        newConso[pdlNumber][months[index].value] = val;
      }
    });
    
    setConsommationsParPDL(newConso);
    alert(`✓ ${values.length} valeurs collées pour le PDL ${pdlNumber}`);
  };

  const handleImportConsommationsOutil = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: false });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        
        console.log('=== IMPORT EXPORT OUTIL ===');
        
        const consommationsGroupees = {};
        let pdlsDetectes = new Set();
        
        // Parser ligne par ligne (skip header)
        for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) {
          // Col E = PDL, Col C = Date, Col D = Consommation
          const pdlCell = worksheet[XLSX.utils.encode_cell({r: rowNum, c: 4})]; // Col E
          const dateCell = worksheet[XLSX.utils.encode_cell({r: rowNum, c: 2})]; // Col C
          const consoCell = worksheet[XLSX.utils.encode_cell({r: rowNum, c: 3})]; // Col D
          
          if (!pdlCell || !dateCell || !consoCell) continue;
          
          const pdl = String(pdlCell.v);
          const dateValue = dateCell.v;
          const conso = parseFloat(consoCell.v);
          
          if (!pdl || !dateValue || isNaN(conso)) continue;
          
          // Parser date
          let moisKey;
          if (typeof dateValue === 'number') {
            const date = new Date((dateValue - 25569) * 86400 * 1000);
            moisKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          } else if (dateValue instanceof Date) {
            moisKey = `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}`;
          } else if (typeof dateValue === 'string' && dateValue.includes('-')) {
            moisKey = dateValue.substring(0, 7);
          }
          
          if (!moisKey) continue;
          
          pdlsDetectes.add(pdl);
          
          if (!consommationsGroupees[pdl]) {
            consommationsGroupees[pdl] = {};
          }
          consommationsGroupees[pdl][moisKey] = conso;
          
          if (rowNum <= 5) {
            console.log(`PDL ${pdl} | ${moisKey} : ${conso.toFixed(2)} kWh`);
          }
        }
        
        console.log(`\n✓ ${pdlsDetectes.size} PDL détectés`);
        console.log(`✓ ${Object.keys(consommationsGroupees).reduce((sum, pdl) => sum + Object.keys(consommationsGroupees[pdl]).length, 0)} lignes de consommation`);
        
        setConsommationsParPDL(consommationsGroupees);
        alert(`✓ Import réussi !\n\n${pdlsDetectes.size} PDL détectés\nConsommations chargées\n\nConsultez le tableau récap pour vérifier`);
        
      } catch (error) {
        console.error('Erreur import:', error);
        alert('❌ Erreur import fichier');
      }
    };
    
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleGenerateRapport = async () => {
    // Validation adaptée au type
    const hasPDL = rapportType === 'mono' 
      ? formData.pdls.length > 0
      : formData.sites.length > 0 && formData.sites.some(s => s.pdls.length > 0);
    
    if (!hasPDL) {
      alert(rapportType === 'mono' ? 'Ajoutez au moins un PDL' : 'Ajoutez au moins un site avec des PDL');
      return;
    }

    // Vérifier consommations
    const allPDLs = rapportType === 'mono' 
      ? formData.pdls
      : formData.sites.flatMap(s => s.pdls);
    
    const hasConsommations = allPDLs.some(pdl => {
      const conso = consommationsParPDL[pdl.pdl];
      return conso && Object.keys(conso).length > 0;
    });

    if (!hasConsommations) {
      if (!window.confirm('Aucune consommation saisie. Continuer quand même ?')) return;
    }

    // Pour les rapports multi-sites en mode SIREN, vérifier d'abord l'éligibilité
    if (rapportType === 'multi' && modeCalcul === 'siren' && formData.sites.length > 1) {
      try {
        const verifyResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/rapports/verify-eligibilite`,
          {
            siren: formData.siren,
            valeur_ajoutee_siren: parseFloat(formData.valeur_ajoutee_siren),
            sites: formData.sites,
            consommations_par_pdl: consommationsParPDL
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        const eligibilite = verifyResponse.data;
        
        // Si NON_ELIGIBLE et plusieurs SIRET, proposer la bascule
        if (eligibilite.peut_basculer_siret) {
          setEligibiliteData(eligibilite);
          setShowModalBasculeSIRET(true);
          return; // Arrêter ici, l'utilisateur choisira dans le modal
        }
      } catch (error) {
        console.error('Erreur vérification éligibilité:', error);
        // Continuer avec la création normale en cas d'erreur
      }
    }

    // Créer le rapport normalement
    await createRapportFinal();
  };

  // Fonction pour créer le rapport après validation
  const createRapportFinal = async (modeOverride = null, resultatsOverride = null) => {
    try {
      const rapportData = {
        siren: formData.siren,
        nom_entreprise: formData.nom_entreprise,
        valeur_ajoutee_siren: parseFloat(formData.valeur_ajoutee_siren),
        debut_periode: new Date(formData.debut_periode).toISOString(),
        fin_periode: new Date(formData.fin_periode).toISOString(),
        agence_id: formData.agence_id,
        commercial_id: formData.commercial_id,
        pdls: rapportType === 'mono' ? formData.pdls : [],
        sites: rapportType === 'multi' ? formData.sites : [],
        consommations_par_pdl: consommationsParPDL,
        type_rapport: rapportType,
        mode_calcul: modeOverride || modeCalcul,
        commentaire_global: formData.commentaire_global || '',
        // Si bascule SIRET, inclure les résultats par SIRET
        resultats_siret: resultatsOverride || null
      };

      // Utiliser PUT si édition, POST si création
      const method = editMode ? 'put' : 'post';
      const url = editMode 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/rapports/${editRapportId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/rapports`;
      
      const response = await axios[method](
        url,
        rapportData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const rapport = response.data;
      console.log('Rapport créé:', rapport);
      
      // Supprimer brouillon si existe
      if (brouillonId) {
        try {
          await apiDeleteBrouillon(brouillonId);
          setBrouillonId(null);
          console.log('✓ Brouillon supprimé');
        } catch (e) {
          console.error('Erreur suppression brouillon:', e);
        }
      }
      
      // Fermer le modal si ouvert
      setShowModalBasculeSIRET(false);
      
      window.open(`/rapports/ticfe/${rapport.id}`, '_blank');
      alert('✓ Rapport généré avec succès !');
      
    } catch (error) {
      console.error('Erreur:', error);
      
      const debugContext = {
        operation: 'Création rapport',
        formData: {
          siren: formData.siren,
          nom_entreprise: formData.nom_entreprise,
          nb_pdls_mono: formData.pdls.length,
          nb_sites_multi: formData.sites.length,
          type: rapportType,
          mode: modeCalcul
        },
        errorResponse: error.response?.data
      };
      
      setErrorContext(debugContext);
      setErrorDebug(error);
      
      alert('❌ ' + (error.response?.data?.detail || error.message));
    }
  };

  // Calculer l'éligibilité par SIRET
  const handleCalculerParSiret = async (mode, vacManuelles, sitesWithVac) => {
    setIsCalculatingSiret(true);
    try {
      // Ajouter les consommations par PDL à chaque site
      const sitesAvecConso = eligibiliteData.siret_groups.map(site => ({
        ...site,
        consommations_pdl: Object.fromEntries(
          site.pdls.map(pdl => [pdl.pdl, consommationsParPDL[pdl.pdl] || {}])
        )
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/rapports/calcul-eligibilite-siret`,
        {
          mode: mode,
          valeur_ajoutee_siren: eligibiliteData.valeur_ajoutee_siren,
          sites: sitesAvecConso,
          vac_manuelles: vacManuelles
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setResultatsParSiret(response.data);
    } catch (error) {
      console.error('Erreur calcul par SIRET:', error);
      alert('Erreur lors du calcul par SIRET');
    } finally {
      setIsCalculatingSiret(false);
    }
  };

  // Confirmer la bascule et créer le rapport
  const handleConfirmBasculeSIRET = async (typeRapport, resultats) => {
    if (typeRapport === 'consolide') {
      // Créer un seul rapport avec tous les SIRET
      await createRapportFinal('siret_consolide', resultats);
    } else {
      // Créer un rapport par SIRET éligible
      for (const siret of resultats.resultats_siret.filter(s => s.eligibilite === 'ELIGIBLE')) {
        const siteSiret = formData.sites.find(s => s.siret === siret.siret);
        if (siteSiret) {
          // Créer un rapport individuel pour ce SIRET
          try {
            const rapportData = {
              siren: formData.siren,
              nom_entreprise: formData.nom_entreprise,
              valeur_ajoutee_siren: siret.vac_siret,
              debut_periode: new Date(formData.debut_periode).toISOString(),
              fin_periode: new Date(formData.fin_periode).toISOString(),
              agence_id: formData.agence_id,
              commercial_id: formData.commercial_id,
              pdls: [],
              sites: [siteSiret],
              consommations_par_pdl: consommationsParPDL,
              type_rapport: 'multi',
              mode_calcul: 'siret_individuel',
              siret_analyse: siret.siret,
              resultats_siret: siret
            };

            const response = await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/rapports`,
              rapportData,
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            window.open(`/rapports/ticfe/${response.data.id}`, '_blank');
          } catch (error) {
            console.error(`Erreur création rapport SIRET ${siret.siret}:`, error);
          }
        }
      }
      
      setShowModalBasculeSIRET(false);
      alert(`✓ ${resultats.nb_siret_eligible} rapport(s) généré(s) pour les SIRET éligibles`);
    }
  };

  const downloadTemplate = () => {
    // Récupérer tous les PDL selon le type de rapport
    const allPDLs = rapportType === 'mono' 
      ? formData.pdls 
      : formData.sites.flatMap(s => s.pdls);
    
    if (allPDLs.length === 0) {
      alert('Ajoutez d\'abord au moins un PDL');
      return;
    }
    
    const months = generateMonthsForPeriod();
    
    // Créer workbook Excel
    const wb = XLSX.utils.book_new();
    
    // Créer feuille avec format : PDL en lignes, Mois en colonnes
    const data = [];
    
    // En-tête : PDL | mois1 | mois2 | ...
    const header = ['PDL'];
    months.forEach(month => {
      header.push(month.label);
    });
    data.push(header);
    
    // Une ligne par PDL
    allPDLs.forEach(pdl => {
      const row = [pdl.pdl];
      months.forEach(() => {
        row.push(0); // Consommation à remplir
      });
      data.push(row);
    });
    
    // Créer feuille
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Largeurs colonnes
    const colWidths = [{ wch: 20 }]; // PDL
    months.forEach(() => colWidths.push({ wch: 12 })); // Mois
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Consommations');
    
    // Télécharger
    XLSX.writeFile(wb, `template_consommations_${formData.siren || 'rapport'}.xlsx`);
    
    alert(`✓ Template Excel téléchargé\n\nFormat : PDL en lignes, Mois en colonnes\n${allPDLs.length} PDL × ${months.length} mois\n\nRemplissez les consommations (en kWh) et réimportez`);
  };

  // Importer les consommations depuis le fichier Excel rempli
  const handleImportConsommationsExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: false });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          alert('❌ Le fichier est vide ou mal formaté');
          return;
        }
        
        // Première ligne = en-têtes (PDL, mois1, mois2, ...)
        const headers = jsonData[0];
        const months = generateMonthsForPeriod();
        
        // Créer mapping mois label -> value
        const monthLabelToValue = {};
        months.forEach(m => {
          monthLabelToValue[m.label.toLowerCase()] = m.value;
        });
        
        const newConsommations = { ...consommationsParPDL };
        let pdlsImportes = 0;
        let valeursImportees = 0;
        
        // Parcourir les lignes (à partir de la 2ème)
        for (let rowIdx = 1; rowIdx < jsonData.length; rowIdx++) {
          const row = jsonData[rowIdx];
          const pdlNumber = row[0] ? String(row[0]).trim() : null;
          
          if (!pdlNumber) continue;
          
          if (!newConsommations[pdlNumber]) {
            newConsommations[pdlNumber] = {};
          }
          
          pdlsImportes++;
          
          // Parcourir les colonnes de consommation
          for (let colIdx = 1; colIdx < row.length && colIdx < headers.length; colIdx++) {
            const headerLabel = headers[colIdx] ? String(headers[colIdx]).toLowerCase().trim() : null;
            const monthValue = monthLabelToValue[headerLabel];
            
            if (monthValue && row[colIdx] !== undefined && row[colIdx] !== null && row[colIdx] !== '') {
              const value = parseFloat(String(row[colIdx]).replace(',', '.').replace(/\s/g, ''));
              if (!isNaN(value)) {
                newConsommations[pdlNumber][monthValue] = value;
                valeursImportees++;
              }
            }
          }
        }
        
        setConsommationsParPDL(newConsommations);
        
        alert(`✓ Import réussi !\n\n${pdlsImportes} PDL importés\n${valeursImportees} valeurs de consommation chargées`);
        
      } catch (error) {
        console.error('Erreur import consommations:', error);
        alert('❌ Erreur lors de la lecture du fichier Excel.\n\nVérifiez que le format correspond au template.');
      }
    };
    
    reader.readAsArrayBuffer(file);
    e.target.value = ''; // Reset input pour permettre de recharger le même fichier
  };

  const handleTypeSelection = (type) => {
    setRapportType(type);
    if (type === 'mono') {
      setModeCalcul('siren'); // Mono = toujours regroupé
      setStep(3); // Passer directement aux données
    } else {
      setStep(2); // Choix mode calcul pour multi-site
    }
  };

  const handleModeSelection = (mode) => {
    setModeCalcul(mode);
    setStep(3);
  };

  // === Contexte transmis aux sous-composants Step4/5/6 ===
  // Un seul prop pour limiter le drilling et garantir que tout est passé.
  const stepCtx = useMemo(() => ({
    formData, setFormData, rapportType, modeCalcul, setStep,
    sauvegarderBrouillon,
    handleTypeSelection, handleModeSelection,
    currentPDL, setCurrentPDL,
    editingPDLIndex, setEditingPDLIndex,
    agences, commerciauxFiltres,
    showAddSite, setShowAddSite,
    showImportSites, setShowImportSites,
    architectureImportee, setArchitectureImportee,
    showRepartitionVAC, setShowRepartitionVAC,
    modeRepartition, setModeRepartition,
    repartitionManuelle, setRepartitionManuelle,
    currentSite, setCurrentSite,
    currentPDLMulti, setCurrentPDLMulti,
    editingPDLMultiIndex, setEditingPDLMultiIndex,
    handleImportSitesExcel, downloadTemplateSites,
    repartirVACAutomatique, validerArchitecture,
    addPDLToSite, addSiteToRapport,
    methodeConsommation, setMethodeConsommation,
    consommationsParPDL, setConsommationsParPDL,
    showTableauRecap, setShowTableauRecap,
    downloadTemplate, generateMonthsForPeriod,
    handleImportConsommationsOutil, handleImportConsommationsExcel,
    updateConsommation,
    nafEligMapTicfe,
    handleGenerateRapport,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    formData, rapportType, modeCalcul,
    currentPDL, editingPDLIndex, agences, commerciauxFiltres,
    showAddSite, showImportSites, architectureImportee,
    showRepartitionVAC, modeRepartition, repartitionManuelle,
    currentSite, currentPDLMulti, editingPDLMultiIndex,
    methodeConsommation, consommationsParPDL, showTableauRecap,
    nafEligMapTicfe,
  ]);

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-4xl font-heading font-semibold text-[#191D1E] mb-2 tracking-tight">
        {editMode ? 'Modifier le Rapport TICFE' : 'Créer un Rapport d\'Éligibilité TICFE'}
      </h1>
      <p className="text-[#687175] mb-8">
        {editMode 
          ? `Modification du rapport — ${formData.nom_entreprise || ''} (${formData.siren || ''})`
          : 'Analyse d\'éligibilité au tarif réduit de la Taxe Intérieure sur la Consommation Finale d\'Électricité'}
      </p>

      {/* Indicateur Mode Édition */}
      {editMode && (
        <div className="mb-6 p-4 bg-[#DBEAFE] border border-[#397DFF] rounded-lg flex items-center gap-3">
          <Pencil className="w-5 h-5 text-[#397DFF]" />
          <div>
            <div className="font-medium text-[#191D1E]">Mode modification</div>
            <div className="text-sm text-[#687175]">Modifiez les informations nécessaires puis cliquez sur "Générer" pour mettre à jour le rapport.</div>
          </div>
        </div>
      )}
      
      {/* Chargement en cours */}
      {loadingEdit && (
        <div className="mb-6 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#397DFF] border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-[#687175]">Chargement du rapport...</div>
        </div>
      )}

      {/* Indicateur Brouillon */}
      {brouillonId && (
        <div className="mb-6 p-4 bg-[#FFF9E6] border border-[#F9C80E] rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">💾</span>
            <div>
              <div className="font-medium text-[#191D1E]">Brouillon en cours</div>
              <div className="text-sm text-[#687175]">Vos modifications sont sauvegardées automatiquement</div>
            </div>
          </div>
          <button
            onClick={supprimerBrouillon}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Supprimer brouillon
          </button>
        </div>
      )}

      {/* Progress */}
      <div className="mb-8 flex items-center gap-2">
        <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#397DFF]' : 'text-[#687175]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 1 ? 'bg-[#397DFF] text-white' : 'bg-[#BFC6CC] text-white'}`}>1</div>
          <span className="text-sm font-medium hidden md:inline">Type</span>
        </div>
        {rapportType === 'multi' && (
          <>
            <div className="h-px flex-1 bg-[#BFC6CC]"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#397DFF]' : 'text-[#687175]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 2 ? 'bg-[#397DFF] text-white' : 'bg-[#BFC6CC] text-white'}`}>2</div>
              <span className="text-sm font-medium hidden md:inline">Mode</span>
            </div>
          </>
        )}
        <div className="h-px flex-1 bg-[#BFC6CC]"></div>
        <div className={`flex items-center gap-2 ${step >= 3 ? 'text-[#397DFF]' : 'text-[#687175]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 3 ? 'bg-[#397DFF] text-white' : 'bg-[#BFC6CC] text-white'}`}>3</div>
          <span className="text-sm font-medium hidden md:inline">Entreprise</span>
        </div>
        <div className="h-px flex-1 bg-[#BFC6CC]"></div>
        <div className={`flex items-center gap-2 ${step >= 4 ? 'text-[#397DFF]' : 'text-[#687175]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 4 ? 'bg-[#397DFF] text-white' : 'bg-[#BFC6CC] text-white'}`}>4</div>
          <span className="text-sm font-medium hidden md:inline">Sites/PDL</span>
        </div>
        <div className="h-px flex-1 bg-[#BFC6CC]"></div>
        <div className={`flex items-center gap-2 ${step >= 5 ? 'text-[#397DFF]' : 'text-[#687175]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 5 ? 'bg-[#397DFF] text-white' : 'bg-[#BFC6CC] text-white'}`}>5</div>
          <span className="text-sm font-medium hidden md:inline">Conso.</span>
        </div>
        <div className="h-px flex-1 bg-[#BFC6CC]"></div>
        <div className={`flex items-center gap-2 ${step >= 6 ? 'text-[#397DFF]' : 'text-[#687175]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 6 ? 'bg-[#397DFF] text-white' : 'bg-[#BFC6CC] text-white'}`}>6</div>
          <span className="text-sm font-medium hidden md:inline">Validation</span>
        </div>
      </div>

      {/* Étape 1 : Choix type rapport */}
      {step === 1 && <Step1Type ctx={stepCtx} />}

      {/* Étape 2 : Mode calcul (Multi-site uniquement) */}
      {step === 2 && rapportType === 'multi' && <Step2ModeCalcul ctx={stepCtx} />}

      {/* Étape 3 : Informations entreprise/sites */}
      {step === 3 && <Step3InfosEntreprise ctx={stepCtx} />}

      {/* Étape 4 : Gestion Sites/PDL (Multi-site) */}
      {step === 4 && rapportType === 'multi' && <Step4Sites ctx={stepCtx} />}


      {/* Étape 5 : Consommations mensuelles par PDL */}
      {step === 5 && <Step5Consommations ctx={stepCtx} />}

      
      {/* Étape 6 : Validation pré-génération */}
      {step === 6 && <Step6Validation ctx={stepCtx} />}
      
      
      {/* Modal Debug Erreur */}
      <ErrorDebugModal 
        error={errorDebug}
        context={errorContext}
        onClose={() => {
          setErrorDebug(null);
          setErrorContext(null);
        }}
      />
      
      {/* Modal Bascule SIRET */}
      <ModalBasculeSIRET
        isOpen={showModalBasculeSIRET}
        onClose={() => {
          setShowModalBasculeSIRET(false);
          setEligibiliteData(null);
          setResultatsParSiret(null);
        }}
        eligibiliteData={eligibiliteData}
        onCalculerParSiret={handleCalculerParSiret}
        resultatsParSiret={resultatsParSiret}
        isCalculating={isCalculatingSiret}
        onConfirm={handleConfirmBasculeSIRET}
      />
      
      {/* Boutons de navigation flottants (haut/bas) */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-10 h-10 bg-white border border-[#BFC6CC] rounded-full shadow-lg flex items-center justify-center hover:bg-[#F5F6F9] transition-colors"
          title="Remonter en haut"
        >
          <svg className="w-5 h-5 text-[#687175]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <button
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          className="w-10 h-10 bg-white border border-[#BFC6CC] rounded-full shadow-lg flex items-center justify-center hover:bg-[#F5F6F9] transition-colors"
          title="Descendre en bas"
        >
          <svg className="w-5 h-5 text-[#687175]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CreateRapport;
